import React, { useState, useEffect, useContext, useRef, useCallback   } from "react";
import { LangContext } from "../../contexts/LangContext";
import { getCookie, getCookieJSON } from "../../services/cookie";
import { getTemplate } from "../Visualization/visualizationTemplates";
import FilterRule from "./FilteringRules/FilterRule";
import { createEmptyRule, countActiveRules } from "./FilteringRules/utils";
import en from "../../i18n/en";
import pl from "../../i18n/pl";
import "../../styles/CreateTask.css";
import "../../styles/ReelsMetrics.css";
import ReelsMetricsSection from "./ReelsMetricsSection";
import { FILTER_METHODS } from "./filterMethodsConfig";


const API_BASE = "https://flautreach.com/flautreach/api";
const STORAGE_KEY = "create_filtering_data";

const getDefaultState = () => ({
  selectedTask: "",
  rules: [createEmptyRule()],
  matchType: "all",
  useAI: false,
  aiGoal: "",
  aiPrompt: "",
  aiMiddlePart: "",
  matchWord: "",
  matchScope: "first_line",
  _aiPromptInitialized: false,
  reelsMetrics: {
    enabled: false,
    minViews: "",
    maxViews: "",
    minLikes: "",
    maxLikes: "",
    minComments: "",
    maxComments: "",
    minEngagementRate: "",
    maxEngagementRate: "",
    engagementFormula: "standard",
    topReelsCount: 30,
    sortBy: "engagement",
    needFilter: false,
    needDescription: false,
    generateSummary: false,
    summaryPrompt: "",
    _summaryPromptInitialized: false,
    filterMethods: [],
  },
});

const getSubtypeFromTask = (task) => {
  if (!task) return null;
  if (task.task_type === "filtering" && task.filtering_config_name?.subtype) {
    return task.filtering_config_name.subtype;
  }
  return task.scraping_subtype?.value || task.subtype || task.search_config?.subtype || null;
};

const getTaskDisplayInfo = (task) => {
  if (!task) return { subtype: null, subtypeLabel: "?", taskType: null, jobsCompleted: 0 };
  const subtype = getSubtypeFromTask(task);
  const subtypeLabel =
    task.filtering_config_name?.subtype ||
    task.scraping_subtype?.label ||
    task.subtype ||
    task.search_config?.subtype ||
    "?";
  return {
    subtype,
    subtypeLabel,
    taskType: task.task_type,
    jobsCompleted: task.jobs_summary?.completed || 0,
    targetTaskId: task.additional_info?.target_task_id,
    filteringConfigName: task.filtering_config_name,
  };
};

const buildAiPrompt = (state, placeholders, r) => {
  const { reelsMetrics } = state;
  const needFilter = reelsMetrics.needFilter;
  const needDescription = reelsMetrics.needDescription;
  
  let part1 = "";
  if (needFilter && needDescription) {
    part1 = r.promptPart1FilterAndDesc;
  } else if (needFilter) {
    part1 = r.promptPart1FilterOnly;
  } else if (needDescription) {
    part1 = r.promptPart1DescOnly;
  }
  
  const part2 = needFilter ? (state.aiMiddlePart || "") : "";
  
  let part3 = "";
  if (needFilter && state.matchWord.trim()) {
    part3 = `\n\n${r.promptPart3PassCondition.replace("{word}", state.matchWord.trim())}`;
  }
  
  return (part1 || "") + (part2 ? part2 + "\n" : "") + (part3 || "");
};




function buildFilteringPayloadFromState(fState, isReels) {
  const activeRules = (fState.rules || []).filter((rule) => rule.field && rule.operator);

  // Массив, а не объект — иначе правила с одинаковым field затирают друг друга
  const rulesArr = activeRules.map((rule) => {
    const item = { field: rule.field, op: rule.operator };
    if (rule.operator === "between") {
      item.value = [rule.valueMin, rule.valueMax];
    } else if (!["is_not_empty", "is_true", "is_false"].includes(rule.operator)) {
      item.value = rule.value;
    }
    return item;
  });

  const filtersData = {
    rules: rulesArr,
    match_type: fState.matchType,
  };

  const rm = fState.reelsMetrics || {};
  if (isReels) {
    filtersData.reels_config = {
      metrics_filter: rm.enabled ? {
        min_views: rm.minViews !== "" ? parseInt(rm.minViews) : null,
        max_views: rm.maxViews !== "" ? parseInt(rm.maxViews) : null,
        min_likes: rm.minLikes !== "" ? parseInt(rm.minLikes) : null,
        max_likes: rm.maxLikes !== "" ? parseInt(rm.maxLikes) : null,
        min_comments: rm.minComments !== "" ? parseInt(rm.minComments) : null,
        max_comments: rm.maxComments !== "" ? parseInt(rm.maxComments) : null,
        min_engagement_rate: rm.minEngagementRate !== "" ? parseFloat(rm.minEngagementRate) : null,
        max_engagement_rate: rm.maxEngagementRate !== "" ? parseFloat(rm.maxEngagementRate) : null,
      } : {},
      engagement_formula: rm.engagementFormula || "standard",
      top_reels_count: parseInt(rm.topReelsCount) || 30,
      sort_by: rm.sortBy || "engagement",
      need_filter: rm.needFilter || false,
      need_description: rm.needDescription || false,
    };
    if (fState.useAI) {
      filtersData.reels_config.ai_filter_prompt = fState.aiMiddlePart || "";
      if (rm.needFilter) filtersData.reels_config.ai_pass_keyword = fState.matchWord || "";
    }
    if (rm.generateSummary && rm.needDescription) {
      filtersData.reels_config.generate_summary = true;
      filtersData.reels_config.summary_prompt = rm.summaryPrompt || "";
    } else {
      filtersData.reels_config.generate_summary = false;
      filtersData.reels_config.summary_prompt = "";
    }
  }

  const filteringPayload = { filters: filtersData, use_ai: fState.useAI };
  if (fState.useAI) {
    filteringPayload.ai_prompt = fState.aiPrompt;
    if (!isReels || rm.needFilter) {
      filteringPayload.ai_pass_keyword = fState.matchWord;
      filteringPayload.check_only_on_first_row = fState.matchScope === "first_line";
    }
  }
  return filteringPayload;
}

export default function FilteringForm({
  onSave,
  onCancel,
  taskName,
  onTestPrompt,
  mode = "standalone",
  scrapingState = null,
  prefillData = null,
  submitRole = "final",
  onStateChange = null,
  isAdbChainStage2 = false,
  isScreenshotChainStage2 = false,
  preAdbFilterState = null,
  preScreenshotFilterState = null,
  stageLabel = null,
  storageKeySuffix = "",
  effectiveSubtypeOverride = null,
}) {
  const { lang } = useContext(LangContext);
  const t = lang === "pl" ? pl : en;
  const f = t.createTaskPage?.filtering || {};
  const r = t.createTaskPage?.instagram_reels || {};
  const token = getCookie("authToken");

  const isIntermediate = submitRole === "intermediate";
  const storageKey = storageKeySuffix ? `${STORAGE_KEY}_${storageKeySuffix}` : STORAGE_KEY;

  const getInitialState = () => {
    if (prefillData) {
      const merged = { ...getDefaultState(), ...prefillData };
      if (!Array.isArray(merged.rules) || merged.rules.length === 0) merged.rules = [createEmptyRule()];
      if (merged._aiPromptInitialized === undefined) merged._aiPromptInitialized = false;
      if (!merged.reelsMetrics) merged.reelsMetrics = getDefaultState().reelsMetrics;
      if (merged.reelsMetrics._summaryPromptInitialized === undefined) merged.reelsMetrics._summaryPromptInitialized = false;
      if (merged.reelsMetrics.needFilter === undefined) merged.reelsMetrics.needFilter = false;
      if (merged.reelsMetrics.needDescription === undefined) merged.reelsMetrics.needDescription = false;
      return merged;
    }
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        const merged = { ...getDefaultState(), ...parsed };
        if (!Array.isArray(merged.rules) || merged.rules.length === 0) merged.rules = [createEmptyRule()];
        if (merged._aiPromptInitialized === undefined) merged._aiPromptInitialized = false;
        if (!merged.reelsMetrics) merged.reelsMetrics = getDefaultState().reelsMetrics;
        if (merged.reelsMetrics._summaryPromptInitialized === undefined) merged.reelsMetrics._summaryPromptInitialized = false;
        if (merged.reelsMetrics.needFilter === undefined) merged.reelsMetrics.needFilter = false;
        if (merged.reelsMetrics.needDescription === undefined) merged.reelsMetrics.needDescription = false;
        return merged;
      }
    } catch (e) {
      console.warn("Failed to parse sessionStorage");
    }
    return getDefaultState();
  };

  const [state, setState] = useState(getInitialState);
  const [tasks, setTasks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [showPromptPreview, setShowPromptPreview] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const selectedTaskId = state.selectedTask;
  const normalizedId = selectedTaskId ? parseInt(selectedTaskId) : null;
  const selectedTaskData = tasks.find((t) => t.id === normalizedId);
  const taskInfo = getTaskDisplayInfo(selectedTaskData);

  // Use override if provided, otherwise determine from mode
  const effectiveSubtype = effectiveSubtypeOverride
    ? effectiveSubtypeOverride
    : mode === "combined"
      ? (scrapingState?.selectedPlatform || null)
      : taskInfo.subtype;

  const isReelsMode = effectiveSubtype === "instagram_reels";

  const reelsMetrics = state.reelsMetrics || getDefaultState().reelsMetrics;

  const currentTemplate = effectiveSubtype ? getTemplate(effectiveSubtype, lang) : null;
  const availableFields = currentTemplate?.basicFilters || [];

  
  const isAiTask =
  selectedTaskData?.filtering_config?.use_ai === true ||
  selectedTaskData?.filtering?.use_ai === true ||
  selectedTaskData?.additional_info?.use_ai === true ||
  selectedTaskData?.filtering_config_name?.use_ai === true;

// Условно добавляем prev_ai_response поле только для AI задач
const availableFieldsWithAI = isAiTask
  ? [
      ...availableFields,
      {
        key: "PREV_AI_RESPONSE",   // ← было PREV_AI_RESPONSE
        label: lang === "pl" ? "Poprzednia odpowiedź AI" : "Previous AI Response",
        type: "text",
        operators: [
          "equals",
          "contains",
          "not_contains",
          "contains_any_row",
          "not_contains_any_row",
          "is_not_empty",
        ],
      },
    ]
  : availableFields;


  const aiVariables = currentTemplate?.aiVariables || [];
  const placeholders = aiVariables.map((f) => ({ key: f.key, label: f.label || f.key }));

  const activeRulesCount = countActiveRules(state.rules);

  const showAiTokens = state.useAI && (reelsMetrics.needFilter || reelsMetrics.needDescription);

  // Main useEffect for resetting/setting AI prompt when platform or language changes
  useEffect(() => {
    if (!state.useAI) return;

    if (prefillData?.aiMiddlePart && !state._aiPromptInitialized) {
      updateState("_aiPromptInitialized", true);
      return;
    }

    if (isReelsMode) {
      const fullPrompt = buildAiPrompt(state, placeholders, r);
      updateState("aiPrompt", fullPrompt);
      if (reelsMetrics.needFilter && !state.aiMiddlePart) {
        updateState("aiMiddlePart", r.aiPromptPlaceholder || "");
      }
    } else {
      if (state.aiPrompt && prefillData?.aiPrompt) {
        return;
      }

      const defaultPrompt = t.defaultPrompts?.[effectiveSubtype] || t.defaultPrompts?.default || "";
      let prompt = defaultPrompt;
      
      updateState("aiPrompt", prompt);
      updateState("aiMiddlePart", "");
    }
  }, [effectiveSubtype, isReelsMode, state.useAI, lang]);

  // Update Reels prompt when parameters change
  useEffect(() => {
    if (!isReelsMode || !state.useAI) return;
    const fullPrompt = buildAiPrompt(state, placeholders, r);
    updateState("aiPrompt", fullPrompt);
  }, [state.aiMiddlePart, reelsMetrics.needFilter, reelsMetrics.needDescription, state.matchWord, state.useAI, isReelsMode, r]);

  useEffect(() => {
    if (isReelsMode && reelsMetrics.generateSummary && reelsMetrics.needDescription && !reelsMetrics._summaryPromptInitialized) {
      updateReelsMetric("summaryPrompt", r.defaultSummaryPrompt);
      updateReelsMetric("_summaryPromptInitialized", true);
    }
    if (!reelsMetrics.generateSummary) {
      updateReelsMetric("_summaryPromptInitialized", false);
    }
  }, [reelsMetrics.generateSummary, reelsMetrics.needDescription, isReelsMode]);

  useEffect(() => {
    if (reelsMetrics.generateSummary && !reelsMetrics.needDescription) {
      updateReelsMetric("needDescription", true);
    }
  }, [reelsMetrics.generateSummary]);

  useEffect(() => {
    if (!reelsMetrics.needDescription && reelsMetrics.generateSummary) {
      updateReelsMetric("generateSummary", false);
    }
  }, [reelsMetrics.needDescription]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      sessionStorage.setItem(storageKey, JSON.stringify(state));
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [state, storageKey]);

  // Lift state up when this instance is the "intermediate" block
  useEffect(() => {
    if (isIntermediate && onStateChange) onStateChange(state);
  }, [state, isIntermediate, onStateChange]);

  useEffect(() => {
    if (mode === "combined" || !token) { setTasksLoading(false); return; }
    setTasksLoading(true);
    fetch(`${API_BASE}/tasks/`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => { setTasks(data); setTasksLoading(false); })
      .catch((err) => { console.error("Failed to load tasks:", err); showError(f.failedToLoadTasks); setTasksLoading(false); });
  }, [token, t, mode]);

  useEffect(() => {
    const handleClear = () => { setState(getDefaultState()); sessionStorage.removeItem(storageKey); };
    window.addEventListener("clearAllTasks", handleClear);
    return () => window.removeEventListener("clearAllTasks", handleClear);
  }, [storageKey]);

  useEffect(() => {
    if (state.useAI && !state.matchWord) {
      updateState("matchWord", t.defaultPrompts?.default_pass_word);
    }
  }, [state.useAI, t]);

  const updateState = useCallback((key, value) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const showError = (message) => {
    setApiError(message);
    setShowErrorModal(true);
  };

  const updateReelsMetric = (key, value) => {
    setState((prev) => ({
      ...prev,
      reelsMetrics: { ...(prev.reelsMetrics || getDefaultState().reelsMetrics), [key]: value },
    }));
  };

  //const handleTaskChange = (id) => { updateState("selectedTask", id); };
   const handleTaskChange = (id) => {
  updateState("selectedTask", id);

  if (id) {
    const normalizedId = parseInt(id);
    const task = tasks.find((t) => t.id === normalizedId);

    if (task) {
      const useAi =
        task?.filtering_config?.use_ai === true ||
        task?.filtering?.use_ai === true ||
        task?.additional_info?.use_ai === true ||
        task?.filtering_config_name?.use_ai === true;

      if (useAi) {
        setState((prev) => {
          const hasPrevAiRule = prev.rules.some(
            (rule) => rule.field === "PREV_AI_RESPONSE"    // ← UPPER
          );
          if (hasPrevAiRule) return prev;

          const prevAiRule = {
            ...createEmptyRule(),
            field: "PREV_AI_RESPONSE",                     // ← UPPER
            operator: "is_not_empty",
          };
          return { ...prev, rules: [prevAiRule, ...prev.rules] };
        });
      } else {
        setState((prev) => ({
          ...prev,
          rules: prev.rules.filter((rule) => rule.field !== "PREV_AI_RESPONSE"),
        }));
      }
    }
  } else {
    setState((prev) => ({
      ...prev,
      rules: prev.rules.filter((rule) => rule.field !== "PREV_AI_RESPONSE"),
    }));
  }
};

  const handleRuleChange = (index, updates) => {
    setState((prev) => ({
      ...prev,
      rules: prev.rules.map((rule, i) => i === index ? { ...rule, ...updates } : rule),
    }));
  };

  const addRule = () => setState((prev) => ({ ...prev, rules: [...prev.rules, createEmptyRule()] }));
  const removeRule = (index) => {
    setState((prev) => ({ ...prev, rules: prev.rules.length <= 1 ? prev.rules : prev.rules.filter((_, i) => i !== index) }));
  };
  const clearAllRules = () => setState((prev) => ({ ...prev, rules: [createEmptyRule()] }));

  const validateAiPlaceholders = (text, placeholders) => {
    if (!text || placeholders.length === 0) return { valid: true, missing: [] };
    
    const hasAny = placeholders.some(({ key, label }) => {
      const placeholder = `[[${key}]]`;
      return text.includes(placeholder);
    });
    
    return {
      valid: hasAny,
      missing: hasAny ? [] : placeholders.map(m => m.key)
    };
  };

  const buildFiltersPayload = () => buildFilteringPayloadFromState(state, isReelsMode).filters;

  const userInfo = getCookieJSON("userInfo");

  const calculateCredits = () => {
    const keywordLines = scrapingState?.keywords?.split("\n").filter((k) => k.trim()).length || 0;
    const pages = parseInt(scrapingState?.advancedSettings?.maxPages) || 1;
    const periods = scrapingState?.advancedSettings?.scrapingPeriods || ["any"];
    const periodsMultiplier = (periods.includes("any") || periods.length === 0) ? 1 : periods.length;
    
    const googleJobs = keywordLines * pages * periodsMultiplier;
    const totalLinks = googleJobs * 10;
    
    const googlePrice = parseFloat(userInfo?.price_per_google_page) || 0.00625;
    const googleCost = googleJobs * googlePrice;
    
    let parsePrice = 0.0007;
    if (userInfo && scrapingState?.selectedPlatform) {
      switch (scrapingState.selectedPlatform) {
        case "facebook_posts": parsePrice = parseFloat(userInfo.price_per_facebook_post) || 0.0007; break;
        case "facebook_pages": parsePrice = parseFloat(userInfo.price_per_facebook_page) || 0.0007; break;
        case "linkedin_posts": parsePrice = parseFloat(userInfo.price_per_linkedin_post) || 0.0007; break;
        case "instagram_posts": parsePrice = parseFloat(userInfo.price_per_instagram_post) || 0.0007; break;
        case "instagram_pages": parsePrice = parseFloat(userInfo.price_per_instagram_page) || 0.0007; break;
        case "instagram_reels": parsePrice = parseFloat(userInfo.price_per_instagram_reel) || parseFloat(userInfo.price_per_instagram_post) || 0.0007; break;
        default: parsePrice = 0.0007; break;
      }
    }
    const parseCost = totalLinks * parsePrice;
    let totalCost = googleCost + parseCost;
    
    if (scrapingState?.selectedPlatform === "instagram_pages" && scrapingState?.parseAuthorPages) {
      totalCost += parseCost;
    }
    
    const isAiEnabled = state.useAI && effectiveSubtype;
    
    if (false) {
      const tokens = Math.round(totalCost * 100);
      return { 
        tokens, 
        keywordLines, 
        pages, 
        totalLinks, 
        periodsMultiplier, 
        showSeparateAiCost: false 
      };
    }
    
    const scrapingTokens = Math.round(totalCost * 100);

    if (isReelsMode) {
      const TOKENS_PER_VIDEO_FILTER = 20000;
      const TOKENS_PER_TEXT_FILTER = 600;
      const TOKENS_PER_MINUTE_DESC = 20000;
      const AVG_REEL_DURATION_SECONDS = 45;
      const estimatedDescTokensPerReel = Math.round((AVG_REEL_DURATION_SECONDS / 60) * TOKENS_PER_MINUTE_DESC);
      const TOKENS_FOR_SUMMARY = 5000;
      const topReelsCount = parseInt(reelsMetrics.topReelsCount) || 30;
      
      let totalFilterTokens = 0;
      let filterDetails = [];
      
      if (reelsMetrics.needFilter) {
        const selectedMethods = reelsMetrics.filterMethods || [];
        
        if (selectedMethods.length > 0) {
          const hasVideoMethod = selectedMethods.includes("video_content");
          const hasUserProfileMethod = selectedMethods.includes("user_profile");
          const hasReelInfoMethod = selectedMethods.includes("reel_info");
          const hasTextMethod = hasUserProfileMethod || hasReelInfoMethod;
          
          let filterPerReel = 0;
          const parts = [];
          
          if (hasVideoMethod) {
            filterPerReel += TOKENS_PER_VIDEO_FILTER;
            parts.push(`Video: ${TOKENS_PER_VIDEO_FILTER.toLocaleString()}/reel`);
          }
          if (hasTextMethod) {
            filterPerReel += TOKENS_PER_TEXT_FILTER;
            const textMethods = [];
            if (hasUserProfileMethod) textMethods.push("profile");
            if (hasReelInfoMethod) textMethods.push("info");
            parts.push(`Text (${textMethods.join("+")}): ${TOKENS_PER_TEXT_FILTER.toLocaleString()}/reel`);
          }
          
          const itemsToFilter = totalLinks;
          totalFilterTokens = itemsToFilter * filterPerReel;
          
          filterDetails.push(`${itemsToFilter.toLocaleString()} reels`);
          filterDetails.push(`  └ ${parts.join(" + ")}`);
          filterDetails.push(`  = ${totalFilterTokens.toLocaleString()} tokens`);
        }
      }
      
      let totalDescTokens = 0;
      let descDetails = [];
      
      if (reelsMetrics.needDescription) {
        totalDescTokens = estimatedDescTokensPerReel * topReelsCount;
        descDetails.push(`${topReelsCount} top reels`);
        descDetails.push(`  └ ${estimatedDescTokensPerReel.toLocaleString()} tokens/reel`);
        descDetails.push(`  = ${totalDescTokens.toLocaleString()} tokens`);
      }
      
      let totalSummaryTokens = 0;
      let summaryDetails = [];
      
      if (reelsMetrics.generateSummary && reelsMetrics.needDescription) {
        totalSummaryTokens = TOKENS_FOR_SUMMARY;
        summaryDetails.push(`1 summary`);
        summaryDetails.push(`  └ ${TOKENS_FOR_SUMMARY.toLocaleString()} tokens`);
      }
      
      const totalAiTokens = totalFilterTokens + totalDescTokens + totalSummaryTokens;
      
      let aiDetails = [];
      if (filterDetails.length > 0) aiDetails.push(...filterDetails);
      if (descDetails.length > 0) aiDetails.push(...descDetails);
      if (summaryDetails.length > 0) aiDetails.push(...summaryDetails);
      if (totalAiTokens > 0) {
        aiDetails.push("");
        aiDetails.push(`Total AI: ${totalAiTokens.toLocaleString()} tokens`);
      }
      
      return {
        scrapingTokens,
        totalAiTokens,
        totalLinks,
        keywordLines,
        pages,
        periodsMultiplier,
        showSeparateAiCost: true,
        platformName: "reels",
        isReelsMode: true,
        topReelsCount,
        totalFilterTokens,
        totalDescTokens,
        totalSummaryTokens,
        aiDetails: aiDetails.join("\n"),
        filterDetails,
        descDetails,
        summaryDetails,
      };
    }

    const AI_TOKENS_PER_ITEM = effectiveSubtype === "instagram_reels" ? 20000 : 1500;
    const OUTPUT_TOKENS = effectiveSubtype === "instagram_reels" ? 250 : 200;
    const OUTPUT_MULTIPLIER = 2;
    
    let itemsToProcess = 0;
    let aiDetails = "";
    let totalAiTokens = 0;
    
    if (isReelsMode && reelsMetrics.needFilter) {
      itemsToProcess = totalLinks;
    } else {
      itemsToProcess = isReelsMode ? (parseInt(reelsMetrics.topReelsCount) || 30) : totalLinks;
    }
    
    totalAiTokens = itemsToProcess * AI_TOKENS_PER_ITEM;
    
    const platformName = currentTemplate?.title || effectiveSubtype;
    aiDetails = `${itemsToProcess} ${platformName} × ${AI_TOKENS_PER_ITEM.toLocaleString()} = ${totalAiTokens.toLocaleString()} tokens`;
    
    if (isReelsMode && reelsMetrics.needDescription) {
      const outputTokens = itemsToProcess * OUTPUT_TOKENS * OUTPUT_MULTIPLIER;
      totalAiTokens += outputTokens;
      aiDetails += `\n+ ${itemsToProcess} × ${OUTPUT_TOKENS * OUTPUT_MULTIPLIER} output = ${outputTokens.toLocaleString()} tokens`;
      aiDetails += `\nTotal: ${totalAiTokens.toLocaleString()} tokens`;
    }

    return {
      scrapingTokens,
      totalAiTokens,
      totalLinks,
      itemsToProcess,
      aiDetails,
      keywordLines,
      pages,
      periodsMultiplier,
      showSeparateAiCost: true,
      platformName,
      isReelsMode,
    };
  };

  const credits = calculateCredits();

  const handleSave = async () => {
    if (mode === "standalone" && !selectedTaskId) { showError(f.selectTaskFirst); return; }
    if (!taskName?.trim()) { showError(f.enterTaskName); return; }
    if (state.useAI && !state.aiPrompt.trim()) { showError(f.enterAiPrompt); return; }
    
    if (state.useAI) {
      if (!state.aiPrompt.trim()) {
        showError(f.enterAiPrompt);
        return;
      }
      
      const placeholdersToCheck = isReelsMode 
        ? placeholders  
        : placeholders;
         
      if (!isReelsMode) {
        const validation = validateAiPlaceholders(
          isReelsMode ? state.aiPrompt : state.aiPrompt,
          placeholdersToCheck
        );
        
        if (!validation.valid) {
          showError(
            (f.missingPlaceholdersError || "Prompt must contain at least one placeholder: {missing}")
              .replace("{missing}", validation.missing.map(m => `[[${m}]]`).join(", "))
          );
          return;
        }
      }
      
      if (isReelsMode && state.useAI && reelsMetrics.needFilter) {
        const selectedMethods = reelsMetrics.filterMethods || [];
        if (selectedMethods.length === 0) {
          showError(r.selectAtLeastOneMethod || "Select at least one filter method");
          setSaving(false);
          return;
        }
        
        const allPlaceholders = [];
        selectedMethods.forEach((methodKey) => {
          const method = FILTER_METHODS[methodKey];
          if (method) {
            method.placeholders.forEach((p) => allPlaceholders.push(p.key));
          }
        });
        
        const hasPlaceholder = allPlaceholders.some((ph) => 
          state.aiMiddlePart.includes(`[[${ph}]]`)
        );
        
        if (!hasPlaceholder) {
          showError(
            (f.missingPlaceholdersError || "Prompt must contain at least one placeholder: {missing}")
              .replace("{missing}", allPlaceholders.map((p) => `[[${p}]]`).join(", "))
          );
          setSaving(false);
          return;
        }
      }
    }

    if (mode === "combined") {
      if (!scrapingState) { showError(f.configureScrapingFirst); return; }
      if (!scrapingState.keywords?.trim()) { showError(f.enterSearchQueries); return; }
      if (!scrapingState.selectedPlatform) { showError(f.selectPlatform); return; }
      if (isAdbChainStage2 && !preAdbFilterState) {
        showError(f.configurePreAdbFilterFirst || "Please configure the filter that runs right after scraping (Step 1) first");
        return;
      }
      if (isScreenshotChainStage2 && !preScreenshotFilterState) {
        showError("Please configure the filter that runs right after scraping (Step 1) first");
        return;
      }
    }

    setSaving(true);
    showError(null);
    
    let payload;

    if (mode === "combined") {
      const keywordList = scrapingState.keywords.split("\n").filter((k) => k.trim());
      
      const VALID_PERIODS = ["h", "d", "w", "m", "y"];
      const rawPeriods = scrapingState.advancedSettings?.scrapingPeriods || ["any"];
      let cleanPeriods;
      if (rawPeriods.includes("any")) {
        cleanPeriods = ["any"];
      } else {
        cleanPeriods = rawPeriods.filter((p) => VALID_PERIODS.includes(p));
        if (cleanPeriods.length === 0) cleanPeriods = ["any"];
      }
      const PERIOD_FIX_MAP = { all: "any", year: "y", month: "m", week: "w", day: "d", hour: "h" };
      cleanPeriods = cleanPeriods.map((p) => PERIOD_FIX_MAP[p] || p);
      cleanPeriods = [...new Set(cleanPeriods)];
      const periodsForApi = cleanPeriods.filter((p) => p !== "any");

      const filtersData = buildFiltersPayload();

      if (isReelsMode) {
        filtersData.reels_config = {
          metrics_filter: reelsMetrics.enabled ? {
            min_views: reelsMetrics.minViews !== "" ? parseInt(reelsMetrics.minViews) : null,
            max_views: reelsMetrics.maxViews !== "" ? parseInt(reelsMetrics.maxViews) : null,
            min_likes: reelsMetrics.minLikes !== "" ? parseInt(reelsMetrics.minLikes) : null,
            max_likes: reelsMetrics.maxLikes !== "" ? parseInt(reelsMetrics.maxLikes) : null,
            min_comments: reelsMetrics.minComments !== "" ? parseInt(reelsMetrics.minComments) : null,
            max_comments: reelsMetrics.maxComments !== "" ? parseInt(reelsMetrics.maxComments) : null,
            min_engagement_rate: reelsMetrics.minEngagementRate !== "" ? parseFloat(reelsMetrics.minEngagementRate) : null,
            max_engagement_rate: reelsMetrics.maxEngagementRate !== "" ? parseFloat(reelsMetrics.maxEngagementRate) : null,
          } : {},
          engagement_formula: reelsMetrics.engagementFormula || "standard",
          top_reels_count: parseInt(reelsMetrics.topReelsCount) || 30,
          sort_by: reelsMetrics.sortBy || "engagement",
          need_filter: reelsMetrics.needFilter || false,
          need_description: reelsMetrics.needDescription || false,
        };

        if (state.useAI) {
          filtersData.reels_config.ai_filter_prompt = state.aiMiddlePart || "";
          if (reelsMetrics.needFilter) {
            filtersData.reels_config.ai_pass_keyword = state.matchWord || "";
          }
        }

        if (reelsMetrics.generateSummary && reelsMetrics.needDescription) {
          filtersData.reels_config.generate_summary = true;
          filtersData.reels_config.summary_prompt = reelsMetrics.summaryPrompt || "";
        } else {
          filtersData.reels_config.generate_summary = false;
          filtersData.reels_config.summary_prompt = "";
        }
      }

      const filteringPayload = {
        filters: filtersData,
        use_ai: state.useAI,
      };

      if (state.useAI) {
        filteringPayload.ai_prompt = state.aiPrompt;
        if (!isReelsMode || reelsMetrics.needFilter) {
          filteringPayload.ai_pass_keyword = state.matchWord;
          filteringPayload.check_only_on_first_row = state.matchScope === "first_line";
        }
      }

      payload = {
        name: taskName || f.scrapingFilteringTaskDefault,
        task_type: "scraping_filtering",
        scraping: {
          subtype: scrapingState.selectedPlatform,
          search_language: scrapingState.advancedSettings?.searchLanguage || "en",
          search_periods: periodsForApi,
          max_pages_per_query: parseInt(scrapingState.advancedSettings?.maxPages) || 1,
        },
        search_queries: keywordList,
        filtering: filteringPayload,
      };

      // ADB Chain - stage 2
      if (isAdbChainStage2) {
        payload.filtering = buildFilteringPayloadFromState(preAdbFilterState, false);
        payload.adb_after_scraping = true;
        payload.post_adb_filtering = filteringPayload;
      }

      // Screenshot Chain - stage 2
      if (isScreenshotChainStage2) {
        payload.filtering = buildFilteringPayloadFromState(preScreenshotFilterState, false);
        payload.facebook_screenshot_after_scraping = true;
        payload.post_screenshot_filtering = filteringPayload;
      }

      if (scrapingState.selectedPlatform === "instagram_pages") {
        payload.additional_info = { 
          instagram_posts_included: scrapingState.parseAuthorPages ? "True" : "False" 
        };
      }

      if (scrapingState.scheduleType === "repeat") {
        payload.schedule = { 
          interval: scrapingState.repeatInterval, 
          count: parseInt(scrapingState.repeatCount) || 1 
        };
      }
    } 
    else {
      const filtersData = buildFiltersPayload();

      if (isReelsMode) {
        filtersData.reels_config = {
          metrics_filter: reelsMetrics.enabled ? {
            min_views: reelsMetrics.minViews !== "" ? parseInt(reelsMetrics.minViews) : null,
            max_views: reelsMetrics.maxViews !== "" ? parseInt(reelsMetrics.maxViews) : null,
            min_likes: reelsMetrics.minLikes !== "" ? parseInt(reelsMetrics.minLikes) : null,
            max_likes: reelsMetrics.maxLikes !== "" ? parseInt(reelsMetrics.maxLikes) : null,
            min_comments: reelsMetrics.minComments !== "" ? parseInt(reelsMetrics.minComments) : null,
            max_comments: reelsMetrics.maxComments !== "" ? parseInt(reelsMetrics.maxComments) : null,
            min_engagement_rate: null,
            max_engagement_rate: null,
          } : {},
          engagement_formula: reelsMetrics.engagementFormula || "standard",
          top_reels_count: parseInt(reelsMetrics.topReelsCount) || 30,
          sort_by: reelsMetrics.sortBy || "engagement",
          need_filter: reelsMetrics.needFilter || false,
          need_description: reelsMetrics.needDescription || false,
        };

        if (state.useAI) {
          filtersData.reels_config.ai_filter_prompt = state.aiMiddlePart || "";
          if (reelsMetrics.needFilter) {
            filtersData.reels_config.ai_pass_keyword = state.matchWord || "";
          }
        }

        if (reelsMetrics.generateSummary && reelsMetrics.needDescription) {
          filtersData.reels_config.generate_summary = true;
          filtersData.reels_config.summary_prompt = reelsMetrics.summaryPrompt || "";
        } else {
          filtersData.reels_config.generate_summary = false;
          filtersData.reels_config.summary_prompt = "";
        }
      }

      const filteringPayload = {
        target_task_id: parseInt(selectedTaskId),
        filters: filtersData,
        use_ai: state.useAI,
      };

      if (state.useAI) {
        filteringPayload.ai_prompt = state.aiPrompt;
        if (!isReelsMode || reelsMetrics.needFilter) {
          filteringPayload.ai_pass_keyword = state.matchWord;
          filteringPayload.check_only_on_first_row = state.matchScope === "first_line";
        }
      }

      payload = {
        name: taskName || `${f.filterPrefix}: ${selectedTaskData?.name || `${f.taskPrefix} #${selectedTaskId}`}`,
        task_type: "filtering",
        filtering: filteringPayload,
        target_task_id: parseInt(selectedTaskId),
      };
    }

    try {
      console.log("🚀 Sending payload:", JSON.stringify(payload, null, 2));
      
      const res = await fetch(`${API_BASE}/tasks/`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`, 
          "Content-Type": "application/json" 
        },
        body: JSON.stringify(payload),
      });
      
      if (!res.ok) { 
        const errData = await res.json().catch(() => ({ detail: "Unknown error" })); 
        throw new Error(JSON.stringify(errData, null, 2)); 
      }
      
      const data = await res.json();
      sessionStorage.removeItem(storageKey);
      
      if (onSave) await onSave(data);
      else window.location.href = "/tasks";
      
    } catch (err) { 
      console.error("Create task error:", err); 
      showError(err.message); 
    } finally { 
      setSaving(false); 
    }
  };

  return (
    <div className="filtering-form-wrap">
      {stageLabel && (
        <div
          className="stage-label-badge"
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--text-secondary)",
            marginBottom: 8,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {stageLabel}
        </div>
      )}

      {apiError && <div className="api-error-box">{apiError}</div>}

      {mode !== "combined" && (
        <div className="section-card">
          <h3>
            {f.selectTaskToFilter}
            <span className="required-badge">*</span>
          </h3>
          <p className="section-sub">{f.chooseExistingTask}</p>
          {tasksLoading ? (
            <div className="reels-loading">{f.loadingTasks}</div>
          ) : (
            <select className="form-input" value={selectedTaskId} onChange={(e) => handleTaskChange(e.target.value)}>
              <option value="">{f.selectTask}</option>
              {tasks.map((task) => {
                const info = getTaskDisplayInfo(task);
                return (
                  <option key={task.id} value={task.id}>
                    {task.name || `${f.task} #${task.id}`} ({info.subtypeLabel})
                    {task.task_type === "filtering" ? " [filtered]" : ""}
                  </option>
                );
              })}
            </select>
          )}
          {currentTemplate && (
            <div className={`task-info-badge ${selectedTaskData?.task_type === "filtering" ? "filtering" : "scraping"}`}>
              {currentTemplate.title}
            </div>
          )}
        </div>
      )}

      {!isReelsMode && currentTemplate && availableFieldsWithAI.length > 0 && (
        <div className="section-card">
          <div className="filter-header-row">
            <div className="filter-title-group">
              <h3>{f.filterBuilder}</h3>
              <p className="filter-title-desc">{f.filterBuilderDesc}</p>
            </div>
            <div className="match-type-toggle">
              <button className={`match-type-btn${state.matchType === "all" ? " selected" : ""}`} onClick={() => updateState("matchType", "all")}>
                {f.allConditions}
              </button>
              <button className={`match-type-btn${state.matchType === "any" ? " selected" : ""}`} onClick={() => updateState("matchType", "any")}>
                {f.anyCondition}
              </button>
            </div>
          </div>

          {activeRulesCount > 1 && (
            <div className={`match-type-hint ${state.matchType === "all" ? "all" : "any"}`}>
              {state.matchType === "all" ? f.allConditionsDesc : f.anyConditionDesc}
            </div>
          )}

          <div className="reels-rules-container">
            {state.rules.map((rule, index) => (
              <FilterRule key={rule.id} rule={rule} index={index} availableFields={availableFieldsWithAI} t={t} onChange={handleRuleChange} onRemove={removeRule} isOnly={state.rules.length <= 1} />
            ))}
          </div>

          <div className="form-actions-row">
            <button className="btn-add-rule" onClick={addRule}>{f.addRule}</button>
            {state.rules.length > 1 && (
              <button className="btn-clear-rules" onClick={clearAllRules}>{f.clearAllRules}</button>
            )}
          </div>
        </div>
      )}

      {isReelsMode && (
        <ReelsMetricsSection
          reelsMetrics={reelsMetrics}
          state={state}
          placeholders={placeholders}
          showPromptPreview={showPromptPreview}
          r={r}
          f={f}
          t={t}
          updateState={updateState}
          updateReelsMetric={updateReelsMetric}
          setShowPromptPreview={setShowPromptPreview}
        />
      )}

      {!isReelsMode && (
        <div className="section-card">
          <h3>
            {f.aiFiltering}
            <span className="optional-badge">{f.optional}</span>
            <label className="reels-enable-toggle">
              <input type="checkbox" checked={state.useAI} onChange={(e) => updateState("useAI", e.target.checked)} />
              <span>{f.enable}</span>
            </label>
          </h3>

          {state.useAI && (
            <div className="reels-ai-content">
              {placeholders.length > 0 && (
                <div className="reels-field-block">
                  <label className="advanced-field-label">{f.availablePlaceholders}</label>
                  <div className="placeholder-buttons">
                    {placeholders.map(({ key, label }) => (
                      <button key={key} className="placeholder-chip" onClick={() => {
                        const ta = document.getElementById("aiPromptTextarea");
                        if (!ta) return;
                        const ph = `[[${key}]]`;
                        const start = ta.selectionStart;
                        updateState("aiPrompt", state.aiPrompt.slice(0, start) + ph + state.aiPrompt.slice(ta.selectionEnd));
                      }} title={label}>[[{key}]] ({label})</button>
                    ))}
                  </div>
                </div>
              )}

              <div className="reels-field-block">
                <label className="advanced-field-label">{f.aiPrompt}</label>
                <textarea id="aiPromptTextarea" className="intent-textarea" placeholder={f.aiPromptPlaceholder} value={state.aiPrompt} onChange={(e) => updateState("aiPrompt", e.target.value)} style={{ minHeight: 320 }} />
              </div>

              <div className="reels-field-block">
                <label className="advanced-field-label">
                  {f.matchCondition}
                  <span className="required-badge">*</span>
                </label>
                <p className="reels-field-hint">{f.matchConditionDesc}</p>
                <input className="form-input" type="text" placeholder={f.wordToMatchPlaceholder} value={state.matchWord} onChange={(e) => updateState("matchWord", e.target.value)} />
                <div className="match-scope-buttons">
                  {["full_text", "first_line"].map((scope) => (
                    <button key={scope} className={`match-scope-btn${state.matchScope === scope ? " selected" : ""}`} onClick={() => updateState("matchScope", scope)}>
                      {scope === "full_text" ? f.entireText : f.firstLineOnly}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!isIntermediate && mode === "combined" && (
        <div className="credits-bar">
          {!credits.showSeparateAiCost ? (
            <div className="credits-row">
              <span className="credits-label">{f.estimatedTokens}</span>
              <span className="credits-value">{credits.tokens}</span>
            </div>
          ) : (
            <>
              <div className="credits-section">
                <div className="credits-section-title">{r.scrapingCost || f.scrapingCost}</div>
                <div className="credits-row">
                  <span className="credits-label">
                    {credits.keywordLines} {r.queries || f.queries} × {credits.pages} {r.pages || f.pages} × {credits.periodsMultiplier} periods = {credits.totalLinks} {credits.platformName}
                  </span>
                  <span className="credits-value">{credits.scrapingTokens.toLocaleString()}</span>
                </div>
              </div>
              
              {credits.isReelsMode ? (
                <>
                  {credits.totalFilterTokens > 0 && (
                    <div className="credits-section">
                      <div className="credits-section-title">{r.aiFilteringCost || "AI Filtering"}</div>
                      <div className="credits-detail-text">{credits.filterDetails.join("\n")}</div>
                    </div>
                  )}
                  
                  {credits.totalDescTokens > 0 && (
                    <div className="credits-section">
                      <div className="credits-section-title">{r.aiDescriptionCost || "AI Description"}</div>
                      <div className="credits-detail-text">{credits.descDetails.join("\n")}</div>
                    </div>
                  )}
                  
                  {credits.totalSummaryTokens > 0 && (
                    <div className="credits-section">
                      <div className="credits-section-title">{r.aiSummaryCost || "AI Summary"}</div>
                      <div className="credits-detail-text">{credits.summaryDetails.join("\n")}</div>
                    </div>
                  )}
                  
                  <div className="credits-section credits-section-total">
                    <div className="credits-section-title">{r.totalAiCost || "Total AI"}</div>
                    <div className="credits-row">
                      <span className="credits-label">Total AI tokens</span>
                      <span className="credits-value">{credits.totalAiTokens.toLocaleString()}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="credits-section">
                  <div className="credits-section-title">{r.aiCost || f.aiCost}</div>
                  <div className="credits-detail-text">{credits.aiDetails}</div>
                </div>
              )}
            </>
          )}
        </div>
      )}        
        
      {!isIntermediate && (
        <div className="form-actions-row">
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={
              saving ||
              !taskName?.trim() ||
              (mode === "standalone" && !selectedTaskId) ||
              (mode === "combined" && (!scrapingState?.keywords?.trim() || !scrapingState?.selectedPlatform)) ||
              (state.useAI && !state.aiPrompt.trim()) ||
              (isReelsMode && reelsMetrics.generateSummary && !reelsMetrics.summaryPrompt.trim()) ||
              (isAdbChainStage2 && !preAdbFilterState) ||
              (isScreenshotChainStage2 && !preScreenshotFilterState)
            }
          >
            {saving ? f.creating : f.createTask}
          </button>
          <button className="btn-cancel" onClick={() => { if (onCancel) onCancel(); else window.location.href = "/tasks"; }}>
            {f.cancel}
          </button>
        </div>
      )}

      {showErrorModal && apiError && (
        <div className="error-modal-overlay" onClick={() => setShowErrorModal(false)}>
          <div className="error-modal" onClick={(e) => e.stopPropagation()}>
            <div className="error-modal-header">
              <span className="error-modal-icon">⚠️</span>
              <h3>{f.errorTitle || "Error"}</h3>
              <button className="error-modal-close" onClick={() => setShowErrorModal(false)}>×</button>
            </div>
            <div className="error-modal-body">
              <p>{apiError}</p>
            </div>
            <div className="error-modal-footer">
              <button className="btn-primary" onClick={() => setShowErrorModal(false)}>
                {f.okButton || "OK"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}